/**
 * EVENTSFLO-CLIENT-PORTAL-PHASE-2-PROJECT-SHIFT-REQUESTS-V1
 * Admin-side Client Requests page.
 *
 * Lists all client-submitted project requests for the current tenant.
 * Admin can change status, add admin notes, and (when rejecting) supply a
 * client-facing rejection reason.
 *
 * No automatic project/shift creation in V1.
 */
import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
// EVENTSFLO-ADMIN-INBOX-CREATE-VENUE-FROM-REQUEST-V1
import { RequestedVenueResolutionPanel } from "../components/admin/RequestedVenueResolutionPanel";
import {
  Loader2,
  Inbox,
  Calendar,
  Users,
  MapPin,
  Building,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
  AlertTriangle,
  Filter,
  Wand2,
  ExternalLink,
} from "lucide-react";
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from "../components/common/GuideButton";

const STATUS_LABELS = {
  submitted: "Submitted",
  under_review: "Under review",
  accepted: "Accepted",
  rejected: "Rejected",
  converted: "Converted to project",
  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1
  cancelled_by_client: "Cancelled by client",
};
const TERMINAL = new Set(["rejected", "converted", "cancelled_by_client"]);

const statusBadge = (status) => {
  switch (status) {
    case "submitted":
      return "bg-blue-500/10 text-blue-600 border-blue-500/20";
    case "under_review":
      return "bg-amber-500/10 text-amber-600 border-amber-500/20";
    case "accepted":
      return "bg-emerald-500/10 text-emerald-600 border-emerald-500/20";
    case "converted":
      return "bg-purple-500/10 text-purple-600 border-purple-500/20";
    case "rejected":
      return "bg-red-500/10 text-red-600 border-red-500/20";
    case "cancelled_by_client":
      return "bg-zinc-500/10 text-zinc-600 border-zinc-500/20";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};
const formatDT = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
};

export default function ClientRequestsAdminPage() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [saving, setSaving] = useState(false);
  // Phase 2.1
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState(null);

  const fetchRequests = useCallback(async (status = "all") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: "1", page_size: "100" });
      if (status && status !== "all") params.append("status", status);
      const r = await api.get(`/admin/project-requests?${params}`);
      setRequests(r.data?.data || []);
      setMeta(r.data?.meta || { total: 0 });
    } catch (err) {
      console.error("Admin requests fetch error:", err);
      toast.error("Failed to load client requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests(statusFilter);
  }, [fetchRequests, statusFilter]);

  const openDetail = (req) => {
    setDetail(req);
    setEditStatus(req.status);
    setAdminNotes(req.admin_notes || "");
    setRejectionReason(req.rejection_reason || "");
    setDetailOpen(true);
  };

  // EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-ACKNOWLEDGE-V1
  const [acknowledging, setAcknowledging] = useState(false);
  const acknowledgeRequest = async () => {
    if (!detail) return;
    setAcknowledging(true);
    try {
      const r = await api.post(
        `/admin/project-requests/${detail.id}/acknowledge`
      );
      setDetail(r.data);
      toast.success("Marked as reviewed. Sidebar badge updated.");
      window.dispatchEvent(
        new CustomEvent("eventsflo:client-requests-pending-count-refresh")
      );
      fetchRequests(statusFilter);
    } catch (err) {
      console.error("Acknowledge error:", err);
      toast.error(err.response?.data?.detail || "Failed to acknowledge");
    } finally {
      setAcknowledging(false);
    }
  };

  const saveChanges = async () => {
    if (!detail) return;
    if (editStatus === "rejected" && !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason (visible to the client).");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (editStatus && editStatus !== detail.status) payload.status = editStatus;
      if (adminNotes !== (detail.admin_notes || "")) payload.admin_notes = adminNotes;
      if (rejectionReason !== (detail.rejection_reason || ""))
        payload.rejection_reason = rejectionReason;
      if (Object.keys(payload).length === 0) {
        toast("No changes to save");
        setSaving(false);
        return;
      }
      const r = await api.patch(`/admin/project-requests/${detail.id}`, payload);
      toast.success("Request updated");
      setDetail(r.data);
      fetchRequests(statusFilter);
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const isTerminal = detail && TERMINAL.has(detail.status);
  const canConvert =
    detail &&
    !TERMINAL.has(detail.status) &&
    detail.status !== "rejected" &&
    !detail.converted_project_id;

  // EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-AND-CONVERT-REVIEW-V1
  // Editable convert payload — initialised from the persisted request
  // when the admin opens the convert review dialog.
  const [convertDraft, setConvertDraft] = useState(null);

  const openConvertDialog = () => {
    if (!detail) return;
    setConvertDraft({
      project_name: detail.project_name || "",
      event_type: detail.event_type || "",
      notes: detail.notes || "",
      meeting_point: detail.meeting_point || "",
      start_date: detail.start_date || "",
      end_date: detail.end_date || "",
      requested_venue_id: detail.requested_venue_id || null,
      requested_venue_location_id: detail.requested_venue_location_id || null,
      on_site_contact: detail.on_site_contact
        ? { ...detail.on_site_contact }
        : { name: "", phone: "", email: "" },
      shifts: (detail.shifts || []).map((s) => ({
        date: s.date || "",
        start_time: s.start_time || "",
        end_time: s.end_time || "",
        staff_required: s.staff_required || 1,
        role: s.role || "",
        activity: s.activity || "",
        location: s.location || "",
        notes: s.notes || "",
        venue_id: s.venue_id || null,
        venue_location_id: s.venue_location_id || null,
        duration_hours: s.duration_hours ?? null,
      })),
    });
    setConvertOpen(true);
  };

  const updateConvertShift = (i, k, v) =>
    setConvertDraft((p) =>
      p
        ? {
            ...p,
            shifts: p.shifts.map((s, idx) =>
              idx === i ? { ...s, [k]: v } : s
            ),
          }
        : p
    );
  const removeConvertShift = (i) =>
    setConvertDraft((p) =>
      p ? { ...p, shifts: p.shifts.filter((_, idx) => idx !== i) } : p
    );

  const handleConvert = async () => {
    if (!detail || !convertDraft) return;
    if (!convertDraft.project_name.trim()) {
      toast.error("Project name is required");
      return;
    }
    if (!convertDraft.shifts.length) {
      toast.error("At least one shift is required");
      return;
    }
    setConverting(true);
    try {
      const payload = {
        project_name: convertDraft.project_name,
        event_type: convertDraft.event_type || null,
        notes: convertDraft.notes || null,
        meeting_point: convertDraft.meeting_point || null,
        start_date: convertDraft.start_date || null,
        end_date: convertDraft.end_date || null,
        requested_venue_id: convertDraft.requested_venue_id || null,
        requested_venue_location_id:
          convertDraft.requested_venue_location_id || null,
        on_site_contact: convertDraft.on_site_contact || null,
        shifts: convertDraft.shifts.map((s) => ({
          ...s,
          staff_required: Number(s.staff_required) || 1,
        })),
      };
      const r = await api.post(
        `/admin/project-requests/${detail.id}/convert`,
        payload
      );
      setConvertResult(r.data);
      const usedTbd = !!r.data.used_tbd_venue;
      const tbdShifts = r.data.used_tbd_shift_count || 0;
      const selectedShifts = r.data.used_selected_venue_count || 0;
      const totalShifts = r.data.shifts_created || 0;
      let msg;
      if (usedTbd) {
        msg = `Draft project created with ${totalShifts} shift${
          totalShifts !== 1 ? "s" : ""
        } — all shifts use TBD Venue. Replace before going live.`;
      } else if (tbdShifts > 0 && selectedShifts > 0) {
        msg = `Draft project created — ${selectedShifts} shift${
          selectedShifts !== 1 ? "s" : ""
        } used the selected venue, ${tbdShifts} fell back to TBD.`;
      } else {
        msg = `Draft project created with ${totalShifts} shift${
          totalShifts !== 1 ? "s" : ""
        } — venue and location set from client request.`;
      }
      toast.success(msg);
      // EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-AND-CONVERT-REVIEW-V1
      // Tell the sidebar to refetch the pending-count immediately so
      // the red badge decrements without waiting for the 60s poll.
      window.dispatchEvent(new CustomEvent("eventsflo:client-requests-pending-count-refresh"));
      // Refresh detail + list
      const refreshed = await api.get(`/admin/project-requests/${detail.id}`);
      setDetail(refreshed.data);
      setEditStatus(refreshed.data.status);
      fetchRequests(statusFilter);
    } catch (err) {
      console.error("Convert error:", err);
      const detailErr = err.response?.data?.detail;
      const msg =
        typeof detailErr === "string"
          ? detailErr
          : detailErr?.message || "Failed to convert request";
      toast.error(msg);
      // If already converted, refresh so the UI reflects state
      if (detailErr?.error === "already_converted") {
        const refreshed = await api.get(`/admin/project-requests/${detail.id}`);
        setDetail(refreshed.data);
        fetchRequests(statusFilter);
      }
    } finally {
      setConverting(false);
      setConvertOpen(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="admin-requests-title">
            Client Requests
          </h1>
          <p className="text-sm text-muted-foreground">
            {meta.total} request{meta.total !== 1 ? "s" : ""} submitted via Client Portal
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
          <GuideButton testId="client-requests-guide-btn" />
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-[180px]"
              data-testid="admin-request-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent
            className="py-16 text-center"
            data-testid="admin-requests-empty"
          >
            <Inbox className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              No client portal requests yet
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {requests.map((req) => (
            <Card
              key={req.id}
              className="hover:bg-accent/30 transition-colors cursor-pointer"
              onClick={() => openDetail(req)}
              data-testid={`admin-request-row-${req.id}`}
            >
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
                    <Inbox className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm">
                        {req.project_name}
                      </h3>
                      <Badge
                        variant="outline"
                        className={`text-[10px] capitalize ${statusBadge(req.status)}`}
                      >
                        {STATUS_LABELS[req.status] || req.status}
                      </Badge>
                      {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1 */}
                      {req.requires_admin_review && req.last_client_action === "edited" && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-amber-500/10 text-amber-700 border-amber-500/30"
                          data-testid={`admin-list-edited-pill-${req.id}`}
                        >
                          Updated by client
                        </Badge>
                      )}
                      {req.requires_admin_review && req.last_client_action === "cancelled" && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-zinc-500/10 text-zinc-700 border-zinc-500/30"
                          data-testid={`admin-list-cancelled-pill-${req.id}`}
                        >
                          Cancelled by client
                        </Badge>
                      )}
                      {/* EVENTSFLO-PUBLIC-QUOTE-TO-DRAFT-PROJECT-REQUEST-V1 */}
                      {req.source === "public_quote_request" && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-sky-500/10 text-sky-700 border-sky-500/30"
                          data-testid={`admin-list-source-public-quote-${req.id}`}
                          title={
                            req.source_public_quote_request_id
                              ? `Public Quote ${req.source_public_quote_request_id}`
                              : "Public Quote"
                          }
                        >
                          Source: Public Quote
                          {req.source_public_quote_request_id
                            ? ` #${String(req.source_public_quote_request_id).slice(0, 6)}`
                            : ""}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        {req.client_name || "—"}
                      </span>
                      <span>
                        by {req.requester_name || req.requester_email || "—"}
                      </span>
                      {req.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(req.start_date)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {req.shifts?.length || 0} shift
                        {(req.shifts?.length || 0) !== 1 ? "s" : ""}
                      </span>
                      {req.venue && (
                        <span className="flex items-center gap-1 truncate max-w-[180px]">
                          <MapPin className="h-3 w-3" />
                          {req.venue}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground shrink-0">
                    {formatDT(req.created_at)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh]"
          data-testid="admin-request-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Inbox className="h-5 w-5 text-primary" />
              {detail?.project_name}
              {detail?.status && (
                <Badge
                  variant="outline"
                  className={`text-[10px] capitalize ${statusBadge(detail.status)}`}
                >
                  {STATUS_LABELS[detail.status] || detail.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {detail && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Client</p>
                    <p className="font-medium">{detail.client_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted by</p>
                    <p className="font-medium">
                      {detail.requester_name || detail.requester_email || "—"}
                    </p>
                    {detail.requester_email && (
                      <p className="text-xs text-muted-foreground">
                        {detail.requester_email}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Event Type</p>
                    <p className="font-medium">{detail.event_type || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted</p>
                    <p className="font-medium">{formatDT(detail.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Start date</p>
                    <p className="font-medium">
                      {detail.start_date ? formatDate(detail.start_date) : "TBC"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">End date</p>
                    <p className="font-medium">
                      {detail.end_date ? formatDate(detail.end_date) : "TBC"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Venue</p>
                    <p className="font-medium">{detail.venue || "—"}</p>
                    {detail.venue_address && (
                      <p className="text-xs text-muted-foreground">
                        {detail.venue_address}
                      </p>
                    )}
                    {/* EVENTSFLO-CLIENT-PORTAL-FLEXIBLE-LOCATION-ENTRY-V1 */}
                    {detail.venue && !detail.requested_venue_id && !detail.venue_resolved && (
                      <Badge
                        variant="outline"
                        className="mt-1 border-amber-500/60 text-amber-600 dark:text-amber-400"
                        data-testid="client-requested-new-venue-badge"
                      >
                        Client requested new venue
                      </Badge>
                    )}
                    {/* EVENTSFLO-ADMIN-INBOX-CREATE-VENUE-FROM-REQUEST-V1 */}
                    {detail.venue_resolved && (
                      <Badge
                        variant="outline"
                        className="mt-1 border-emerald-500/60 text-emerald-600 dark:text-emerald-400"
                        data-testid="requested-venue-resolved-badge"
                      >
                        Venue resolved
                      </Badge>
                    )}
                    {detail.venue && !detail.requested_venue_id && !detail.venue_resolved && (
                      <RequestedVenueResolutionPanel
                        request={detail}
                        onResolved={async () => {
                          // Re-fetch detail + list so badges and panel disappear.
                          try {
                            const fresh = await api.get(
                              `/admin/project-requests/${detail.id}`,
                            );
                            setDetail(fresh.data);
                            await fetchRequests(statusFilter);
                          } catch (e) {
                            /* swallow — fetchRequests already toasts */
                          }
                        }}
                      />
                    )}
                  </div>
                  {detail.meeting_point && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">
                        Meeting Point
                      </p>
                      <p className="font-medium">{detail.meeting_point}</p>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <h4 className="text-sm font-semibold mb-2">
                    Shifts ({detail.shifts?.length || 0})
                  </h4>
                  <div className="space-y-2">
                    {(detail.shifts || []).map((s, i) => (
                      <div
                        key={i}
                        className="p-3 border border-border/60 rounded-md text-xs"
                      >
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-medium">
                          <span>{formatDate(s.date)}</span>
                          <span>
                            {s.start_time} – {s.end_time}
                          </span>
                          <Badge variant="secondary" className="text-[10px]">
                            {s.staff_required}× staff
                          </Badge>
                          {s.role && (
                            <span className="text-muted-foreground">
                              {s.role}
                            </span>
                          )}
                        </div>
                        {(s.activity || s.location) && (
                          <p className="text-muted-foreground mt-1">
                            {s.activity}
                            {s.location ? ` • ${s.location}` : ""}
                          </p>
                        )}
                        {s.notes && (
                          <p className="text-muted-foreground mt-1 italic">
                            {s.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {detail.on_site_contact && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        On-site Contact
                      </p>
                      <p className="font-medium">
                        {detail.on_site_contact.name || "—"}
                      </p>
                      <div className="text-xs text-muted-foreground">
                        {detail.on_site_contact.phone && (
                          <span>{detail.on_site_contact.phone}</span>
                        )}
                        {detail.on_site_contact.email && (
                          <span className="ml-2">
                            {detail.on_site_contact.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {detail.notes && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        Client Notes
                      </p>
                      <p className="text-sm whitespace-pre-wrap">
                        {detail.notes}
                      </p>
                    </div>
                  </>
                )}

                <Separator />

                {/* Admin actions */}
                <div className="space-y-3 bg-muted/30 p-3 rounded-md">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    Admin actions
                    {isTerminal && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-muted text-muted-foreground"
                      >
                        Locked (terminal status)
                      </Badge>
                    )}
                  </h4>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select
                      value={editStatus}
                      onValueChange={setEditStatus}
                      disabled={isTerminal}
                    >
                      <SelectTrigger data-testid="admin-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {editStatus === "rejected" && (
                    <div>
                      <Label className="text-xs">
                        Rejection reason{" "}
                        <span className="text-muted-foreground">
                          (visible to client)
                        </span>
                      </Label>
                      <Textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        maxLength={2000}
                        disabled={isTerminal}
                        data-testid="admin-rejection-reason"
                        placeholder="Explain to the client why this request can't be accepted."
                      />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">
                      Admin notes{" "}
                      <span className="text-muted-foreground">
                        (internal only — never shown to client)
                      </span>
                    </Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      maxLength={4000}
                      data-testid="admin-notes-input"
                      placeholder="Internal notes for your team."
                    />
                  </div>
                </div>

                {detail.status_updated_at && (
                  <p className="text-[10px] text-muted-foreground">
                    Last status change: {formatDT(detail.status_updated_at)}
                  </p>
                )}

                {/* Phase 2.1: Converted project link + Phase 2.2: open buttons */}
                {detail.converted_project_id && (
                  <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-md space-y-2">
                    <p className="text-xs font-semibold text-purple-700">
                      Converted to draft project
                    </p>
                    <p className="text-[11px] font-mono text-purple-700/80 break-all">
                      {detail.converted_project_id}
                    </p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        data-testid="open-converted-project-link"
                      >
                        <a
                          href={`/projects/${detail.converted_project_id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                          Open Project
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        data-testid="open-in-planner-link"
                      >
                        <a
                          href={`/planner/sheet?project_id=${detail.converted_project_id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                          Open in Planner
                        </a>
                      </Button>
                    </div>
                    {/* EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-AND-CONVERT-REVIEW-V1
                        TBD warning is now driven by the backend's conversion_meta.
                        Shown only when at least one shift actually fell back to
                        the TBD venue. Otherwise show a normal confirmation. */}
                    {detail.conversion_meta?.used_tbd_venue ? (
                      <div
                        className="mt-2 flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-[11px] text-amber-800"
                        data-testid="tbd-venue-warning"
                      >
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>
                          All shifts in this draft used <strong>TBD Venue / TBD Meeting Point</strong>.
                          Replace venue and location before confirming with the
                          client.
                        </span>
                      </div>
                    ) : detail.conversion_meta?.used_tbd_shift_count > 0 ? (
                      <div
                        className="mt-2 flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-[11px] text-amber-800"
                        data-testid="tbd-venue-warning-partial"
                      >
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>
                          {detail.conversion_meta.used_tbd_shift_count} of{" "}
                          {detail.conversion_meta.used_tbd_shift_count +
                            detail.conversion_meta.used_selected_venue_count}{" "}
                          shifts used TBD Venue / TBD Meeting Point because
                          venue/location was missing or no longer available.
                          Replace those rows before going live.
                        </span>
                      </div>
                    ) : (
                      <div
                        className="mt-2 flex items-start gap-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-[11px] text-emerald-800"
                        data-testid="venue-from-request-info"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>
                          Venue and location selected from client request.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => setDetailOpen(false)}
              data-testid="admin-detail-close-btn"
            >
              Close
            </Button>
            <div className="flex gap-2">
              {/* EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-ACKNOWLEDGE-V1 */}
              {detail?.requires_admin_review && (
                <Button
                  variant="outline"
                  onClick={acknowledgeRequest}
                  disabled={acknowledging}
                  data-testid="admin-acknowledge-btn"
                >
                  {acknowledging ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Mark as reviewed
                </Button>
              )}
              {canConvert && (
                <Button
                  variant="secondary"
                  onClick={openConvertDialog}
                  disabled={converting}
                  data-testid="convert-to-draft-btn"
                >
                  {converting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  Convert to draft project
                </Button>
              )}
              <Button
                onClick={saveChanges}
                disabled={saving || isTerminal}
                data-testid="admin-save-btn"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-AND-CONVERT-REVIEW-V1 */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent
          className="sm:max-w-3xl max-h-[90vh] flex flex-col"
          data-testid="convert-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              Review and create draft project
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-1">
              Edit any field below before creating the draft. Nothing is saved
              until you click <strong>Create draft project</strong>.
            </DialogDescription>
          </DialogHeader>
          {convertDraft && (
            <ScrollArea className="flex-1 pr-3">
              <div className="space-y-4 py-2">
                {/* Top-level project */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Project name *</Label>
                    <Input
                      value={convertDraft.project_name}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          project_name: e.target.value,
                        }))
                      }
                      data-testid="convert-project-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Event type</Label>
                    <Input
                      value={convertDraft.event_type}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          event_type: e.target.value,
                        }))
                      }
                      data-testid="convert-event-type"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Meeting point</Label>
                    <Input
                      value={convertDraft.meeting_point}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          meeting_point: e.target.value,
                        }))
                      }
                      data-testid="convert-meeting-point"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Start date</Label>
                    <Input
                      type="date"
                      value={convertDraft.start_date}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          start_date: e.target.value,
                        }))
                      }
                      data-testid="convert-start-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">End date</Label>
                    <Input
                      type="date"
                      value={convertDraft.end_date}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          end_date: e.target.value,
                        }))
                      }
                      data-testid="convert-end-date"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Project notes</Label>
                    <Textarea
                      value={convertDraft.notes}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          notes: e.target.value,
                        }))
                      }
                      rows={2}
                      data-testid="convert-notes"
                    />
                  </div>
                </div>

                {/* Venue summary */}
                <div className="rounded border border-border bg-card/40 p-2 text-xs">
                  <div className="flex items-center gap-2 font-medium mb-1">
                    <MapPin className="h-3.5 w-3.5" />
                    Venue & location (from client request)
                  </div>
                  <div className="text-muted-foreground">
                    {detail?.requested_venue_name ? (
                      <span data-testid="convert-venue-name">
                        Venue:{" "}
                        <strong className="text-foreground">
                          {detail.requested_venue_name}
                        </strong>
                        {detail.requested_venue_location_name && (
                          <>
                            {" · "}Location:{" "}
                            <strong className="text-foreground">
                              {detail.requested_venue_location_name}
                            </strong>
                          </>
                        )}
                      </span>
                    ) : (
                      <span
                        className="text-amber-700"
                        data-testid="convert-no-venue"
                      >
                        No venue selected — shifts will fall back to TBD
                        Venue. Edit the request and add a venue if you want a
                        real one used.
                      </span>
                    )}
                  </div>
                </div>

                {/* On-site contact */}
                <div className="rounded border border-border bg-card/40 p-2">
                  <div className="flex items-center gap-2 text-xs font-medium mb-2">
                    <Users className="h-3.5 w-3.5" />
                    On-site contact
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input
                      placeholder="Name"
                      value={convertDraft.on_site_contact?.name || ""}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          on_site_contact: {
                            ...(p.on_site_contact || {}),
                            name: e.target.value,
                          },
                        }))
                      }
                      data-testid="convert-osc-name"
                    />
                    <Input
                      placeholder="Phone"
                      value={convertDraft.on_site_contact?.phone || ""}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          on_site_contact: {
                            ...(p.on_site_contact || {}),
                            phone: e.target.value,
                          },
                        }))
                      }
                      data-testid="convert-osc-phone"
                    />
                    <Input
                      placeholder="Email"
                      value={convertDraft.on_site_contact?.email || ""}
                      onChange={(e) =>
                        setConvertDraft((p) => ({
                          ...p,
                          on_site_contact: {
                            ...(p.on_site_contact || {}),
                            email: e.target.value,
                          },
                        }))
                      }
                      data-testid="convert-osc-email"
                    />
                  </div>
                </div>

                {/* Editable shifts */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-medium mb-2">
                    <Clock className="h-3.5 w-3.5" />
                    Shifts ({convertDraft.shifts.length})
                  </div>
                  <div className="space-y-2">
                    {convertDraft.shifts.map((s, i) => (
                      <div
                        key={i}
                        className="rounded border border-border bg-card/40 p-2 space-y-2"
                        data-testid={`convert-shift-row-${i}`}
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                          <div>
                            <Label className="text-[10px]">Date</Label>
                            <Input
                              type="date"
                              value={s.date}
                              onChange={(e) =>
                                updateConvertShift(i, "date", e.target.value)
                              }
                              data-testid={`convert-shift-date-${i}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">Start</Label>
                            <Input
                              type="time"
                              value={s.start_time}
                              onChange={(e) =>
                                updateConvertShift(
                                  i,
                                  "start_time",
                                  e.target.value
                                )
                              }
                              data-testid={`convert-shift-start-${i}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">End</Label>
                            <Input
                              type="time"
                              value={s.end_time}
                              onChange={(e) =>
                                updateConvertShift(
                                  i,
                                  "end_time",
                                  e.target.value
                                )
                              }
                              data-testid={`convert-shift-end-${i}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">Staff</Label>
                            <Input
                              type="number"
                              min={1}
                              value={s.staff_required}
                              onChange={(e) =>
                                updateConvertShift(
                                  i,
                                  "staff_required",
                                  e.target.value
                                )
                              }
                              data-testid={`convert-shift-staff-${i}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">Role</Label>
                            <Input
                              value={s.role}
                              onChange={(e) =>
                                updateConvertShift(i, "role", e.target.value)
                              }
                              placeholder="e.g. Server"
                              data-testid={`convert-shift-role-${i}`}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <Input
                            value={s.activity}
                            onChange={(e) =>
                              updateConvertShift(i, "activity", e.target.value)
                            }
                            placeholder="Activity"
                            data-testid={`convert-shift-activity-${i}`}
                          />
                          <Input
                            value={s.location}
                            onChange={(e) =>
                              updateConvertShift(i, "location", e.target.value)
                            }
                            placeholder="Location (free text)"
                            data-testid={`convert-shift-location-${i}`}
                          />
                          <div className="flex items-center justify-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeConvertShift(i)}
                              data-testid={`convert-shift-remove-${i}`}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" />
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
          <DialogFooter className="mt-3">
            <Button
              variant="outline"
              onClick={() => setConvertOpen(false)}
              disabled={converting}
              data-testid="convert-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConvert}
              disabled={converting}
              data-testid="convert-confirm-btn"
            >
              {converting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Wand2 className="h-4 w-4 mr-2" />
              Create draft project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
